import { useRef, useState } from "react";
import {
  FileText,
  Activity,
  RotateCcw,
  Trash2,
  Settings,
  Upload,
  File,
  Check,
} from "lucide-react";
import ConfirmDeleteModal from "../companies/ConfirmDeleteModel"; // Adjust path as needed

export default function FileSection({
  hasFile,
  files = [],
  uploading,
  onUpload,
  onReindex,
  onDelete,
}) {
  const fileInputRef = useRef(null);
  const fileInputInitialRef = useRef(null);
  
  // State for delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(fileToDelete);
      setShowDeleteModal(false);
      setToastMessage(`File "${fileToDelete.fileName}" deleted successfully`);
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setToastMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error deleting file:", error);
      setToastMessage("Failed to delete file. Please try again.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setToastMessage("");
      }, 3000);
    } finally {
      setIsDeleting(false);
      setFileToDelete(null);
    }
  };

  return (
    <>
      <section>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <FileText size={20} className="text-[#36a852]" /> Knowledge Base
            (Files)
          </h3>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
              onChange={onUpload}
              className="hidden"
              id="file-upload"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload"
              className={`text-sm font-semibold text-[#36a852] flex items-center gap-1.5 bg-[#36a852]/10 dark:bg-[#36a852]/20 px-3 py-1.5 rounded-lg transition-colors hover:bg-[#36a852]/20 cursor-pointer ${
                uploading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#36a852] border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} /> Upload Documents
                </>
              )}
            </label>
          </div>
        </div>

        {hasFile && files.length > 0 ? (
          <div className="bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
            {/* Vector DB header */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center">
              <div>
                <p className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                  <Activity size={16} className="text-green-500" /> Vector
                  Database Active
                </p>
                <p className="text-xs text-slate-500 mt-1 pl-6">
                  {files.length} document{files.length !== 1 ? "s" : ""} indexed
                </p>
              </div>
              <button className="p-2 bg-slate-100 dark:bg-slate-700 text-slate-500 hover:text-[#36a852] rounded-lg transition-colors">
                <Settings size={16} />
              </button>
            </div>

            {/* File list */}
            <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {files.map((file, idx) => (
                <li
                  key={`${file.fileName}-${idx}`}
                  className="p-5 flex items-center gap-3 hover:bg-white dark:hover:bg-slate-800 transition-colors group"
                >
                  {/* File icon */}
                  <div className="w-8 h-8 rounded-lg bg-[#36a852]/10 text-[#36a852] flex items-center justify-center flex-shrink-0">
                    <File size={16} />
                  </div>

                  {/* File name + path */}
                  <div className="flex-1 min-w-0">
                    <span
                      className="text-sm font-semibold text-slate-700 dark:text-slate-300 block truncate"
                      title={file.fileName}
                    >
                      {file.fileName}
                    </span>
                    <span
                      className="text-xs text-slate-500 block truncate"
                      title={file.filePath}
                    >
                      {file.filePath}
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onReindex(file)}
                      className="px-2.5 py-1.5 bg-slate-100 hover:bg-[#36a852] hover:text-white dark:bg-slate-700 dark:hover:bg-[#36a852] text-slate-600 dark:text-slate-300 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1"
                    >
                      <RotateCcw size={13} /> Re-index
                    </button>
                    <button
                      onClick={() => handleDeleteClick(file)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Summary footer */}
            <div className="px-5 py-3 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                {files.length} file{files.length !== 1 ? "s" : ""} in knowledge
                base
              </span>
              <span className="inline-flex px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-500 font-bold text-xs rounded-md border border-green-200 dark:border-green-800">
                {files.length} Indexed
              </span>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-3xl p-10 text-center flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900/30">
            <div className="w-16 h-16 rounded-full bg-[#36a852]/10 text-[#36a852] flex items-center justify-center mb-4">
              <FileText size={32} />
            </div>
            <p className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">
              Knowledge Base not enabled
            </p>
            <p className="text-sm text-slate-500 mb-6 max-w-sm">
              Upload documents to enable document indexing and retrieval for this
              tenant.
            </p>
            <div>
              <input
                ref={fileInputInitialRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
                onChange={onUpload}
                className="hidden"
                id="file-upload-initial"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload-initial"
                className={`px-6 py-2.5 bg-[#36a852] text-white hover:bg-[#2e9046] rounded-full text-sm font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2 cursor-pointer ${
                  uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} /> Upload First Document
                  </>
                )}
              </label>
            </div>
          </div>
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {fileToDelete && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setFileToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemName={fileToDelete?.fileName || "this file"}
          itemType="file"
          isDeleting={isDeleting}
        />
      )}

      {/* Success Toast Notification */}
      {showToast && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div className="w-7 h-7 rounded-full bg-[#36a852] flex items-center justify-center flex-shrink-0">
            <Check size={14} strokeWidth={3} />
          </div>
          <span className="text-sm font-semibold">{toastMessage}</span>
        </div>
      )}
    </>
  );
}